import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronDown, Check } from 'lucide-react';
import ThemeSelector from './ThemeSelector';
import AuthComponent from './AuthComponent';
import { useAuth } from '../hooks/useAuth';

interface HeaderProps {
  onSidebarToggle?: () => void;
  isSidebarOpen?: boolean;
}

const OrgSwitcher = () => {
  const { orgs, orgId, switchOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!orgs.length) return null;

  const current = orgs.find((o) => o.id === orgId);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors"
        style={{
          border: '1px solid var(--border)',
          color: 'var(--text-primary)',
          backgroundColor: open ? 'var(--bg-secondary)' : 'transparent',
        }}
      >
        <Building2 className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />
        <span className="font-medium max-w-[80px] sm:max-w-[140px] truncate">
          {current?.name ?? 'Select Org'}
        </span>
        {orgs.length > 1 && (
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
            style={{ color: 'var(--text-secondary)' }}
          />
        )}
      </button>

      {open && orgs.length > 1 && (
        <div
          className="absolute top-full left-0 mt-1 w-56 rounded-xl shadow-lg z-50 py-1 overflow-hidden"
          style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border)' }}
        >
          <p
            className="text-[10px] font-semibold uppercase tracking-wide px-3 py-2"
            style={{ color: 'var(--text-secondary)' }}
          >
            Switch Organisation
          </p>
          {orgs.map((org) => (
            <button
              key={org.id}
              onClick={() => { switchOrg(org); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 transition-colors text-left"
              style={{ color: 'var(--text-primary)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--bg-secondary)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold"
                style={{ backgroundColor: 'var(--accent-light, #e0f2fe)', color: 'var(--accent)' }}
              >
                {org.name[0].toUpperCase()}
              </div>
              <span className="text-sm font-medium flex-1 truncate">{org.name}</span>
              {org.id === orgId && <Check className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--accent)' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = ({ onSidebarToggle, isSidebarOpen = false }: HeaderProps) => {
  return (
    <header
      className="sticky top-0 z-40 border-b shadow-sm"
      style={{
        backgroundColor: 'var(--bg-primary)',
        borderColor: 'var(--border)',
        color: 'var(--text-primary)',
      }}
    >
      <div className="mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {!isSidebarOpen && (
              <button
                onClick={onSidebarToggle}
                className="p-2 rounded-md hover:bg-opacity-10 shrink-0"
                style={{
                  color: 'var(--text-primary)',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                }}
                aria-label="Open sidebar"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}

            <Link to="/" className="flex items-center space-x-1 sm:space-x-2 no-underline shrink-0">
              <div className="text-xl sm:text-2xl">📦</div>
              <h1 className="text-lg sm:text-xl font-bold m-0 hidden xs:block sm:block" style={{ color: 'var(--accent)' }}>
                Sudo IMS
              </h1>
            </Link>

            <OrgSwitcher />
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <div className="hidden sm:block">
              <ThemeSelector />
            </div>
            <AuthComponent />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
